import { useMemo } from "react";
import { formatUserTimeWithZone } from "./Utilities/timezone";
import { useLiveSentiment } from "./Utilities/useLiveSentiment";
import { useResolvedDark } from "../../ThemeProvider/ThemeContext";

const POSITIVE_SENTIMENT_COLOR = "#35C48B";
const NEGATIVE_SENTIMENT_COLOR = "#E25B5B";
const NEUTRAL_SENTIMENT_COLOR = "#43CFE6";

const getSentimentTone = (value) => {
  if (value == null) return NEUTRAL_SENTIMENT_COLOR;
  if (value > 0.05) return POSITIVE_SENTIMENT_COLOR;
  if (value < -0.05) return NEGATIVE_SENTIMENT_COLOR;
  return NEUTRAL_SENTIMENT_COLOR;
};

const getSentimentLabel = (value) => {
  if (value == null) return "Unavailable";
  if (value >= 0.35) return "Bullish";
  if (value <= -0.35) return "Bearish";
  return "Neutral";
};

const getSentimentTrend = (delta) => {
  if (delta == null || Math.abs(delta) < 0.03) {
    return "Stable";
  }

  return delta > 0 ? "Improving" : "Softening";
};

const getSurfaceTheme = (isDark) => ({
  panelBg: isDark ? "#12172A" : "#FFFFFF",
  panelAltBg: isDark ? "#0F1424" : "#F8FAFC",
  border: isDark ? "#1E2642" : "#E9ECEF",
  text: isDark ? "#E8ECF8" : "#0B1220",
  muted: isDark ? "#A6B0CF" : "#5B6472",
});

const CoTraderLiveSentimentBar = ({
  isDark: isDarkProp,
  sentiment,
  sentimentUpdatedAt,
  symbolKey = "",
}) => {
  const isDark = useResolvedDark(isDarkProp);
  const theme = getSurfaceTheme(isDark);

  const { value: liveSentimentValue, delta: sentimentDelta } = useLiveSentiment(
    sentiment,
    symbolKey,
  );
  const liveSentimentColor = useMemo(
    () => getSentimentTone(liveSentimentValue),
    [liveSentimentValue],
  );
  const liveSentimentLabel = useMemo(
    () => getSentimentLabel(liveSentimentValue),
    [liveSentimentValue],
  );
  const liveSentimentIndicatorPosition =
    liveSentimentValue == null ? 50 : ((liveSentimentValue + 1) / 2) * 100;
  const liveSentimentTrend = useMemo(
    () => getSentimentTrend(sentimentDelta),
    [sentimentDelta],
  );
  const liveSentimentUpdatedLabel = useMemo(() => {
    if (sentimentUpdatedAt == null) {
      return "Waiting for stream";
    }

    return formatUserTimeWithZone(sentimentUpdatedAt);
  }, [sentimentUpdatedAt]);

  return (
    <div className="px-3 py-2" style={{ background: theme.panelBg }}>
      <div
        className="rounded-4 border d-flex flex-column gap-2 px-3 py-2"
        style={{
          background:
            liveSentimentValue == null
              ? theme.panelAltBg
              : `${liveSentimentColor}10`,
          borderColor:
            liveSentimentValue == null ? theme.border : `${liveSentimentColor}33`,
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span
              style={{
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: theme.muted,
              }}
            >
              Live Sentiment
            </span>
            <span
              className="badge rounded-pill"
              style={{
                background: `${liveSentimentColor}16`,
                border: `1px solid ${liveSentimentColor}33`,
                color: liveSentimentColor,
                fontWeight: 700,
              }}
            >
              {liveSentimentLabel}
            </span>
          </div>

          <div
            style={{
              color: theme.text,
              fontSize: "0.92rem",
              fontWeight: 700,
            }}
          >
            {liveSentimentValue == null ? "--" : liveSentimentValue.toFixed(4)}
          </div>
        </div>

        <div
          style={{
            position: "relative",
            height: 8,
            borderRadius: 999,
            overflow: "hidden",
            background:
              "linear-gradient(90deg, rgba(226,91,91,0.28) 0%, rgba(67,207,230,0.18) 50%, rgba(53,196,139,0.28) 100%)",
            border: `1px solid ${theme.border}`,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "50%",
              width: 1,
              background: "rgba(91, 100, 114, 0.28)",
              transform: "translateX(-50%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: `${liveSentimentIndicatorPosition}%`,
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: liveSentimentColor,
              border: `2px solid ${theme.panelBg}`,
              transform: "translate(-50%, -50%)",
              boxShadow: `0 0 0 3px ${liveSentimentColor}22`,
            }}
          />
        </div>

        <div
          className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
          style={{
            color: theme.muted,
            fontSize: "0.74rem",
            fontWeight: 600,
          }}
        >
          <span>{liveSentimentTrend}</span>
          <span>{liveSentimentUpdatedLabel}</span>
        </div>
      </div>
    </div>
  );
};

export default CoTraderLiveSentimentBar;
